/**
 * Upload / delete against the Cloudflare Worker media API.
 * Never talks to R2 directly — credentials stay on the Worker.
 */

import { auth } from "../../firebase/config";
import {
  MEDIA_KIND_CONFIG,
  assertAcceptedImageFile,
  assertSafeMediaPath,
  mediaObjectPath,
} from "./mediaTypes";
import { compressImageForMedia } from "./imageCompression";
import { friendlyMediaError, stripMediaCacheBust } from "./mediaUrl";

const UPLOAD_TIMEOUT_MS = 60_000;

function mediaApiBase() {
  const raw = String(process.env.REACT_APP_MEDIA_API || "").trim().replace(/\/+$/, "");
  return raw || null;
}

/**
 * @returns {Promise<string>}
 */
async function getIdToken() {
  const user = auth?.currentUser;
  if (!user) {
    const err = new Error("Sign in required to upload media");
    err.code = "auth_required";
    throw err;
  }
  return user.getIdToken(/* forceRefresh */ true);
}

/**
 * Normalize Worker JSON (new ApiResponse envelope or legacy flat body).
 * @param {object | null} body
 * @param {Response} res
 */
function unwrapMediaBody(body, res) {
  if (body && body.success === true && body.data) return body.data;
  if (body && body.success === false) {
    const msg = body.error?.message || "Upload failed";
    const err = new Error(msg);
    err.code = body.error?.code || `http_${res.status}`;
    err.status = res.status;
    throw err;
  }
  return body;
}

/**
 * @param {string} path
 * @param {RequestInit} init
 */
async function mediaFetch(path, init = {}) {
  const base = mediaApiBase();
  if (!base) {
    const err = new Error("Media API is not configured");
    err.code = "media_api_missing";
    throw err;
  }
  const token = await getIdToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${base}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      const err = new Error("Upload timed out");
      err.code = "timeout";
      throw friendlyMediaError(err);
    }
    throw friendlyMediaError(e);
  } finally {
    clearTimeout(timer);
  }

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg =
      body?.error?.message ||
      body?.error ||
      body?.message ||
      (res.status === 401
        ? "Sign in required"
        : res.status === 403
          ? "You don’t have permission to upload this media"
          : "Upload failed");
    const err = new Error(typeof msg === "string" ? msg : "Upload failed");
    err.code = body?.error?.code || body?.code || `http_${res.status}`;
    err.status = res.status;
    throw friendlyMediaError(err);
  }
  return unwrapMediaBody(body, res);
}

/**
 * Compress + upload via Worker. Returns public CDN URL (no cache-bust query;
 * callers append photoUpdatedAt / logoUpdatedAt).
 * @param {{
 *   kind: string,
 *   entityId: string,
 *   file: File,
 *   onProgress?: (phase: string, detail?: object) => void,
 * }} input
 * @returns {Promise<{ url: string, path: string }>}
 */
export async function uploadMediaViaWorker({ kind, entityId, file, onProgress }) {
  assertAcceptedImageFile(file);
  const cfg = MEDIA_KIND_CONFIG[kind];
  if (!cfg) throw new Error("Unknown media kind");

  onProgress?.("compressing");
  const { blob, mime } = await compressImageForMedia(file, {
    kind,
    onProgress,
  });

  onProgress?.("uploading", { bytes: blob.size });
  const form = new FormData();
  form.append("file", blob, cfg.fileName);
  form.append("entityId", entityId);
  form.append("kind", kind);
  form.append("contentType", mime);

  const endpoint =
    kind === "player"
      ? "/api/upload/player"
      : kind === "organization"
        ? "/api/upload/organization"
        : "/api/upload/competition";

  const result = await mediaFetch(endpoint, {
    method: "POST",
    body: form,
  });

  const url = result?.url;
  const path = result?.path || mediaObjectPath(kind, entityId);
  if (!url || typeof url !== "string") {
    throw new Error("Upload succeeded but no public URL was returned");
  }
  return { url: stripMediaCacheBust(url), path };
}

/**
 * @param {string} path R2 object key (e.g. players/pid/avatar.webp)
 */
export async function deleteMediaViaWorker(path) {
  const safe = assertSafeMediaPath(path);
  await mediaFetch(`/api/media/${encodeURIComponent(safe)}`, {
    method: "DELETE",
  });
}

/**
 * @param {string} path
 * @param {string} [publicBase] override PUBLIC_MEDIA_URL
 */
export function getPublicUrl(path, publicBase) {
  const safe = assertSafeMediaPath(path);
  const base = String(
    publicBase || process.env.REACT_APP_MEDIA_PUBLIC_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");
  if (!base) return `/${safe}`;
  return `${base}/${safe}`;
}

export function isMediaApiConfigured() {
  return Boolean(mediaApiBase());
}
